import { useTheme } from '@shopify/restyle';

import type { PropsWithChildren } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { View } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';
import { Text } from '../text';
import type { Theme } from 'app/shopify';

export const CALENDAR_DAY_SIZE = 40;

export interface CalendarDayProps {
  disabled?: boolean;
  onPress?: () => void;
  selected?: boolean;
  style?: StyleProp<ViewStyle>;
  variant: {
    availability: 'available' | 'highlighted-available' | 'unavailable' | 'selectable-unavailable' | 'current-loan';
    month: 'current' | 'adjacent';
    rangePosition: 'none' | 'start' | 'end' | 'start-and-end';
  };
  willNowChoose?: 'start' | 'end';
  testID?: string;
}

function getCalendarDayState({
  disabled,
  selected,
  variant,
  willNowChoose,
}: Pick<CalendarDayProps, 'disabled' | 'selected' | 'variant' | 'willNowChoose'>) {
  const isAvailable = variant.availability === 'highlighted-available';
  const unavailable = variant.availability === 'unavailable';
  const selectableUnavailable = variant.availability === 'selectable-unavailable';
  const isPartOfUserCurrentLoan = variant.availability === 'current-loan';
  const isOutOfMonth = variant.month === 'adjacent';
  const isStart = variant.rangePosition === 'start' || variant.rangePosition === 'start-and-end';
  const isEnd = variant.rangePosition === 'end' || variant.rangePosition === 'start-and-end';
  const isConflict = Boolean(selected && unavailable && !selectableUnavailable);
  const highlightStartOrEnd = (willNowChoose === 'start' && isStart) || (willNowChoose === 'end' && isEnd);

  return {
    disabled,
    highlightStartOrEnd,
    isAvailable,
    isConflict,
    isOutOfMonth,
    isPartOfUserCurrentLoan,
    selectableUnavailable,
    selected,
    unavailable,
  };
}

type CalendarDayState = ReturnType<typeof getCalendarDayState>;

function getButtonBackgroundColor(state: CalendarDayState, theme: Theme) {
  if (state.selected) {
    if (state.isConflict) return theme.colors['surface.brand.secondary-4.default'];
    if (state.disabled) return theme.colors['surface.inverse.tertiary-neutral'];
    if (state.highlightStartOrEnd) return theme.colors['surface.brand.default'];
    return theme.colors['surface.tertiary'];
  }

  if (state.unavailable) return theme.colors['surface.brand.secondary-4.default'];
  if (state.selectableUnavailable) return theme.colors['surface.warning.subtle'];
  if (state.isPartOfUserCurrentLoan) return theme.colors['surface.tertiary-neutral'];
  if (state.disabled) return undefined;
  if (state.isAvailable) return theme.colors['surface.brand.secondary-1.default'];
  return undefined;
}

function getTextColor(state: CalendarDayState, buttonBackgroundColor: string | undefined, theme: Theme) {
  if (state.isOutOfMonth) return 'text.placeholder' as const;
  if (state.disabled) {
    if (state.isConflict || state.unavailable || state.selectableUnavailable) return 'text.primary' as const;
    if (buttonBackgroundColor === theme.colors['surface.tertiary-neutral']) return 'surface.primary-neutral' as const;
    if (buttonBackgroundColor === undefined) return 'text.placeholder' as const;
    return 'text.inverse.primary' as const;
  }
  if (state.selected) {
    if (state.isConflict) return 'surface.brand.default' as const;
    if (state.highlightStartOrEnd) return 'surface.primary-neutral' as const;
    return 'text.brand' as const;
  }
  if (state.unavailable) return 'text.placeholder' as const;
  if (state.selectableUnavailable) return 'text.warning' as const;
  return 'text.primary' as const;
}

function ConflictHighlight({ isConflict, theme }: { isConflict: boolean; theme: Theme }) {
  if (!isConflict) return null;

  return (
    <>
      <View
        style={{
          backgroundColor: theme.colors['surface.tertiary'],
          height: '100%',
          width: '100%',
          position: 'absolute',
          borderRadius: theme.borderRadii['radius.xl'],
        }}
      />
      <View
        style={{
          backgroundColor: theme.colors['surface.brand.secondary-4.default'],
          height: '75%',
          width: '75%',
          position: 'absolute',
          borderRadius: theme.borderRadii['radius.xl'] * 0.75,
        }}
      />
    </>
  );
}

export function CalendarDay({
  children,
  disabled,
  onPress,
  selected,
  style,
  variant,
  willNowChoose,
  testID,
}: PropsWithChildren<CalendarDayProps>) {
  const theme = useTheme<Theme>();
  const dayState = getCalendarDayState({ disabled, selected, variant, willNowChoose });
  const buttonBackgroundColor = getButtonBackgroundColor(dayState, theme);
  const textColor = getTextColor(dayState, buttonBackgroundColor, theme);

  return (
    <Pressable
      {...{ disabled, onPress, testID }}
      style={[
        {
          alignItems: 'center',
          aspectRatio: 1,
          backgroundColor: buttonBackgroundColor,
          justifyContent: 'center',
          height: CALENDAR_DAY_SIZE,
          width: CALENDAR_DAY_SIZE,
          borderRadius: 9999,
          opacity: dayState.isConflict || dayState.unavailable ? 0.45 : 1,
        },
        style,
      ]}
    >
      <ConflictHighlight isConflict={dayState.isConflict} theme={theme} />
      <Text
        color={textColor}
        fontWeight={
          dayState.isConflict || selected || dayState.unavailable || dayState.selectableUnavailable ? '700' : '500'
        }
        variant="bodyBase"
      >
        {children}
      </Text>
    </Pressable>
  );
}
